import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useApp } from '../../context/AppContext';
import { Headset, MessageCircle, Check, CheckCheck, Clock, Volume2, VolumeX } from 'lucide-react';
import { toast } from 'sonner';
import { playChime } from '../../lib/chime';

const SOUND_KEY = 'chatbot_alert_sound';

const API = process.env.REACT_APP_BACKEND_URL;
const POLL_MS = 30_000;

const ago = (iso) => {
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

// Header bell for live-agent requests coming from the ChatBot Hub webhook.
export const ChatbotAlertBell = () => {
  const { currentUser } = useApp();
  const navigate = useNavigate();
  const [data, setData] = useState({ visible: false, count: 0, requests: [] });
  const [open, setOpen] = useState(false);
  const [flash, setFlash] = useState(false);
  const [sound, setSound] = useState(() => localStorage.getItem(SOUND_KEY) !== 'off');
  const soundRef = useRef(sound);
  useEffect(() => { soundRef.current = sound; localStorage.setItem(SOUND_KEY, sound ? 'on' : 'off'); }, [sound]);
  const prevCount = useRef(0);
  const ref = useRef(null);
  const uid = currentUser?.id;

  const refresh = useCallback(async () => {
    if (!uid) return;
    try {
      const { data: d } = await axios.get(`${API}/api/chatbot/agent-requests/pending`, { params: { requester_id: uid } });
      setData(d);
      if (prevCount.current >= 0 && d.count > prevCount.current) {
        setFlash(true);
        if (soundRef.current) playChime();
        const first = d.requests?.[0];
        toast.warning(`${first?.req_name || first?.req_mobile || 'A customer'} requested a live agent`, { id: 'chatbot-alert', duration: 6000 });
      }
      prevCount.current = d.count;
    } catch { /* best-effort */ }
  }, [uid]);

  useEffect(() => {
    if (!uid) return undefined;
    prevCount.current = -1;
    refresh();
    const id = setInterval(refresh, POLL_MS);
    const onVis = () => { if (document.visibilityState === 'visible') refresh(); };
    document.addEventListener('visibilitychange', onVis);
    return () => { clearInterval(id); document.removeEventListener('visibilitychange', onVis); };
  }, [uid, refresh]);

  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  if (!data.visible) return null;

  const handle = async (ids) => {
    try {
      await axios.post(`${API}/api/chatbot/agent-requests/handle`, { requester_id: uid, ids });
      setFlash(false);
      refresh();
    } catch { toast.error('Could not update request'); }
  };
  const openChat = (ids) => { setOpen(false); if (ids?.length) handle(ids); navigate('/support-chat'); };
  const active = data.count > 0;

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        data-testid="chatbot-alert-bell"
        title={active ? `${data.count} customer${data.count === 1 ? '' : 's'} waiting for a live agent` : 'Support Chat — no pending requests'}
        onClick={() => { setOpen(o => !o); setFlash(false); }}
        className={`relative p-2 rounded-lg transition-colors ${active ? 'bg-rose-50 hover:bg-rose-100' : 'hover:bg-slate-50'}`}
      >
        <Headset className={`w-5 h-5 ${active ? 'text-rose-600' : 'text-slate-500'} ${flash && active ? 'animate-bounce' : ''}`} strokeWidth={1.5} />
        {active && (
          <span data-testid="chatbot-alert-count" className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-600 text-white text-[10px] font-bold flex items-center justify-center ring-2 ring-white">
            {data.count > 99 ? '99+' : data.count}
            <span className="absolute inset-0 rounded-full bg-rose-500 animate-ping opacity-60" />
          </span>
        )}
      </button>

      {open && (
        <div data-testid="chatbot-alert-panel" className="absolute right-0 top-12 w-[22rem] bg-white border border-border rounded-xl shadow-lg overflow-hidden z-50">
          <div className="p-3.5 border-b border-border/40 flex items-center justify-between">
            <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2"><Headset className="w-4 h-4 text-rose-600" /> Live agent requests</h3>
            <div className="flex items-center gap-2">
              <button data-testid="chatbot-alert-sound-toggle" title={sound ? 'Chime on — click to mute' : 'Chime muted — click to enable'}
                onClick={() => { const next = !sound; setSound(next); if (next) playChime(); }}
                className={`p-1 rounded-md transition-colors ${sound ? 'text-emerald-600 hover:bg-emerald-50' : 'text-slate-400 hover:bg-slate-100'}`}>
                {sound ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
              </button>
              {active && (
                <button data-testid="chatbot-alert-handle-all" onClick={() => handle([])} className="text-xs text-slate-500 hover:text-slate-800 font-medium inline-flex items-center gap-1"><CheckCheck className="w-3.5 h-3.5" /> Clear all</button>
              )}
            </div>
          </div>
          <div className="max-h-80 overflow-y-auto">
            {!active ? (
              <p className="p-5 text-sm text-slate-400 text-center">No customers waiting right now</p>
            ) : data.requests.map(r => (
              <div key={r.id} data-testid={`chatbot-alert-item-${r.id}`} className="p-3 border-b border-border/20 hover:bg-rose-50/40 transition-colors">
                <div className="flex items-start gap-2.5">
                  <div className="w-8 h-8 rounded-full bg-rose-100 text-rose-700 flex items-center justify-center font-bold text-xs flex-shrink-0">{(r.req_name || r.req_mobile || '?').charAt(0).toUpperCase()}</div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-slate-800 truncate">{r.req_name || r.req_mobile || 'Customer'}</p>
                    <p className="text-xs text-slate-500 truncate">{[r.req_mobile && r.req_name ? r.req_mobile : null, r.platform_name, r.channel_name].filter(Boolean).join(' · ')}</p>
                    <p className="text-[11px] text-slate-400 mt-0.5 inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {ago(r.received_at)}{r.event === 'agent_request_pending' ? ' · still waiting' : ''}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 mt-2 pl-[42px]">
                  <button data-testid={`chatbot-alert-open-${r.id}`} onClick={() => openChat([r.id])} className="inline-flex items-center gap-1 text-xs font-semibold text-white bg-rose-600 hover:bg-rose-700 rounded-md px-2.5 py-1"><MessageCircle className="w-3.5 h-3.5" /> Open chat</button>
                  <button data-testid={`chatbot-alert-handle-${r.id}`} onClick={() => handle([r.id])} className="inline-flex items-center gap-1 text-xs font-medium text-slate-600 hover:text-slate-900 rounded-md px-2 py-1 hover:bg-slate-100"><Check className="w-3.5 h-3.5" /> Handled</button>
                </div>
              </div>
            ))}
          </div>
          <button data-testid="chatbot-alert-open-support" onClick={() => openChat([])} className="w-full p-2.5 text-xs font-semibold text-rose-700 hover:bg-rose-50 border-t border-border/40">Open Support Chat</button>
        </div>
      )}
    </div>
  );
};
